import axios from "axios";

const API_URL = `${process.env.REACT_APP_API_URL}/api`;

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
});

// Product API calls
export const productApi = {
  // Get all products
  getAllProducts: async () => {
    try {
      const response = await api.get("/products");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new product (with image upload)
  // Updated createProduct method
  createProduct: async (productData) => {
    try {
      console.log("🔍 productData:", productData);

      const formData = new FormData();
      formData.append("title", productData.title);
      formData.append("description", productData.description);
      formData.append("status", productData.status ?? true);

      // Use the prices array directly if it's already in the correct format
      const pricesArray = Array.isArray(productData.prices)
        ? productData.prices
        : Object.entries(productData.prices || {}).map(([_, data]) => ({
            name: data.size,
            value: data.price,
            stock: data.stock,
          }));

      formData.append("prices", JSON.stringify(pricesArray));
      console.log("✅ Prices appended:", pricesArray);

      // Handle image upload (multiple images)
      if (productData.images && productData.images.length > 0) {
        console.log("🖼️ Raw images to append:", productData.images);

        for (const [index, imageWrapper] of productData.images.entries()) {
          if (!imageWrapper) {
            console.warn(`⚠️ Image at index ${index} is null or undefined`);
            continue;
          }

          console.log(`🔎 Image wrapper at index ${index}:`, imageWrapper);

          // Handle if imageWrapper is a { file: File } structure
          let image = null;

          if (
            imageWrapper instanceof File ||
            (typeof imageWrapper === "string" && imageWrapper)
          ) {
            image = imageWrapper;
          } else if (
            imageWrapper?.file instanceof File ||
            (typeof imageWrapper?.file === "string" && imageWrapper?.file)
          ) {
            image = imageWrapper.file;
          }

          // Now determine how to handle it
          if (image instanceof File || image instanceof Blob) {
            console.log(`📁 Appending File/Blob at index ${index}:`, image);
            formData.append("images", image);
          } else if (typeof image === "string" && image.startsWith("data:")) {
            try {
              const response = await fetch(image);
              const blob = await response.blob();
              const file = new File([blob], `image_${index}.jpg`, {
                type: "image/jpeg",
              });
              console.log(
                `🧪 Created File from base64 at index ${index}:`,
                file
              );
              formData.append("images", file);
            } catch (err) {
              console.warn(
                `⚠️ Failed to convert base64 at index ${index}`,
                err
              );
            }
          } else {
            console.warn(
              `⚠️ Skipping unsupported image format at index ${index}:`,
              imageWrapper
            );
          }
        }
      } else {
        console.log("ℹ️ No images to append.");
      }

      // Debug: list all formData entries
      console.log("📦 Final FormData contents:");
      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(
            `${key}: File -> name: ${value.name}, type: ${value.type}, size: ${value.size}`
          );
        } else {
          console.log(`${key}:`, value);
        }
      }

      // Debug: list all image files specifically
      console.log("📸 Images in FormData:");
      const allImages = formData.getAll("images");
      allImages.forEach((img, i) => {
        if (img instanceof File) {
          console.log(
            `Image #${i}: name=${img.name}, type=${img.type}, size=${img.size}`
          );
        } else {
          console.log(`Image #${i}:`, img);
        }
      });

      // Important: let Axios handle multipart content-type automatically
      const response = await api.post("/products", formData); // no headers!
      console.log("✅ Product created successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Error creating product:", error);
      throw error;
    }
  },

  // Updated updateProduct method
  updateProduct: async (id, productData) => {
    try {
      console.log("🔍 Original productData:", productData);
      const formData = new FormData();
      formData.append("title", productData.title);
      formData.append("description", productData.description);
      formData.append("status", productData.status);

      // Handle prices - preserve existing prices if no new prices are provided
      let pricesArray;
      if (Array.isArray(productData.prices) && productData.prices.length > 0) {
        // If prices array exists and has items, use it
        pricesArray = productData.prices.map((price) => ({
          name: price.name || price.size || "", // Ensure name is never undefined
          value: price.value || price.price || 0,
          stock: price.stock || 0,
        }));
      } else if (
        typeof productData.prices === "object" &&
        Object.keys(productData.prices || {}).length > 0
      ) {
        // If prices object exists and has items, convert it
        pricesArray = Object.entries(productData.prices).map(([_, data]) => ({
          name: data.size || "", // Ensure name is never undefined
          value: data.price || 0,
          stock: data.stock || 0,
        }));
      } else {
        // If no prices provided, use empty array to preserve existing prices
        pricesArray = [];
      }

      // Filter out any prices without a name
      const validPricesArray = pricesArray.filter(
        (price) => price.name && price.name.trim() !== ""
      );

      console.log("💰 Prices array before append:", validPricesArray);
      formData.append("prices", JSON.stringify(validPricesArray));

      // Handle images
      if (productData.images && productData.images.length >= 0) {
        // Get existing images that should be kept (only URLs, not blobs or data URLs)
        const existingImages = productData.images
          .filter(img => 
            typeof img === "string" && 
            img && 
            !img.startsWith("blob:") && 
            !img.startsWith("data:")
          );

        console.log("📸 Images to keep:", existingImages);
        formData.append("existingImages", JSON.stringify(existingImages));

        // Handle new image uploads
        for (const [index, imageWrapper] of productData.images.entries()) {
          if (!imageWrapper) {
            console.warn(`⚠️ Skipping null/undefined image at index ${index}`);
            continue;
          }

          // Skip if it's an existing image URL
          if (
            typeof imageWrapper === "string" &&
            imageWrapper &&
            !imageWrapper.startsWith("blob:") &&
            !imageWrapper.startsWith("data:")
          ) {
            console.log(`📝 Skipping existing image URL at index ${index}`);
            continue;
          }

          // Handle new image formats
          if (imageWrapper instanceof File) {
            console.log(`📁 Appending new File object at index ${index}`);
            formData.append("images", imageWrapper);
          } else if (imageWrapper?.file instanceof File) {
            console.log(`📁 Appending new File from wrapper at index ${index}`);
            formData.append("images", imageWrapper.file);
          } else if (imageWrapper?.url && typeof imageWrapper.url === "string" && imageWrapper.url.startsWith("blob:")) {
            console.log(`🔄 Converting blob URL to File at index ${index}`);
            try {
              const response = await fetch(imageWrapper.url);
              const blob = await response.blob();
              const file = new File([blob], `image_${index}.jpg`, {
                type: "image/jpeg",
              });
              console.log(`✅ Created File from blob at index ${index}:`, file);
              formData.append("images", file);
            } catch (err) {
              console.warn(`❌ Failed to convert blob at index ${index}:`, err);
            }
          }
        }
      }

      // Log the final form data for debugging
      console.log("📦 Final FormData contents:");
      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(
            `${key}: File -> name: ${value.name}, type: ${value.type}, size: ${value.size}`
          );
        } else {
          console.log(`${key}:`, value);
        }
      }

      const response = await api.put(`/products/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      console.log("✅ Update successful:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Error updating product:", error);
      if (error.response) {
        console.error("Server response:", error.response.data);
      }
      throw error;
    }
  },

  // Delete product
  deleteProduct: async (id) => {
    try {
      const response = await api.delete(`/products/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Optional: separate image upload (not needed if included in createProduct)
  uploadImage: async (id, imageFile) => {
    try {
      const formData = new FormData();
      formData.append("image", imageFile);

      const response = await api.post(`/products/${id}/image`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getProductById: async (id) => {
    try {
      if (!id) {
        throw new Error("Product ID is required");
      }
      const response = await api.get(`/products/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch product"
      );
    }
  },
};

// Auth API
export const authApi = {
  signIn: async (credentials) => {
    try {
      const response = await api.post("/auth/signin", credentials);
      if (response.data.success) {
        // Store token with expiration time
        const expiresAt = new Date().getTime() + 60 * 60 * 1000; // 1 hour from now
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("tokenExpiresAt", expiresAt.toString());
        localStorage.setItem("user", JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: "An error occurred" };
    }
  },

  signOut: () => {
    localStorage.removeItem("token");
    localStorage.removeItem("tokenExpiresAt");
    localStorage.removeItem("user");
  },

  isAuthenticated: () => {
    const token = localStorage.getItem("token");
    const expiresAt = localStorage.getItem("tokenExpiresAt");

    if (!token || !expiresAt) {
      return false;
    }

    // Check if token is expired
    if (new Date().getTime() > parseInt(expiresAt)) {
      authApi.signOut();
      window.location.href = "/sign-in";
      return false;
    }

    return true;
  },
};

// Update axios instance to include auth token and handle expiration
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    const expiresAt = localStorage.getItem("tokenExpiresAt");

    if (token && expiresAt) {
      // Check if token is expired
      if (new Date().getTime() > parseInt(expiresAt)) {
        authApi.signOut();
        window.location.href = "/sign-in";
        return Promise.reject("Token expired");
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      authApi.signOut();
      window.location.href = "/sign-in";
    }
    return Promise.reject(error);
  }
);

export default api;
